import { useCallback, useEffect, useState } from 'react';
import { coreAPI } from '../../lib/api';

export interface UserOption {
  value: string;
  label: string;
}

/**
 * Active users of `companyId` as picker options (action owners).
 *
 * @param companyId - the auditing customer; null = nothing loaded
 * @returns options, loading / error flags and `retry`
 */
export function useCompanyUsers(companyId: number | null) {
  const [options, setOptions] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [attempt, setAttempt] = useState<number>(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    coreAPI.getCompanyUsers(companyId)
      .then((response) => {
        if (cancelled) return;
        setOptions(response.data
          .filter((profile) => profile.user?.is_active)
          .map((profile) => ({
            value: String(profile.user.id),
            label: profile.user.full_name || profile.user.email,
          })));
      })
      .catch((err: unknown) => {
        console.error('Failed to load users:', err);
        if (!cancelled) setError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, attempt]);

  return { options, loading, error, retry };
}
